import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { EnterpriseLayout } from "@/components/layout/EnterpriseLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Users, User, Mail, Phone, Package, DollarSign,
    Search, Shield, ShieldOff, Circle, Clock, UserCheck,
    UserX, RefreshCw, Wifi, MessageCircle, Send, Bell
} from "lucide-react";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// WhatsApp reminder message templates
const REMINDER_TEMPLATES = {
    payment: (buyerName, orderInfo) =>
        `Bonjou ${buyerName}!\n\nSa a se yon rapel pou peman kòmand ou a${orderInfo ? ` (${orderInfo})` : ''}.\n\nTanpri fè peman an pi vit posib pou nou ka pase kòmand la sou Amazon pou ou.\n\nMèsi!\n- BVGLINKAMAZON`,
    order_ready: (buyerName) =>
        `Bonjou ${buyerName}!\n\nKòmand ou a prè! Tanpri konekte sou BVGLINKAMAZON pou wè detay yo.\n\nMèsi!\n- BVGLINKAMAZON`,
    welcome: (buyerName) =>
        `Bonjou ${buyerName}!\n\nBienveni sou BVGLINKAMAZON! Nou la pou ede ou achte sou Amazon fasilman.\n\nSi ou gen nenpòt kesyon, pa ezite kontakte nou!\n\nMèsi!\n- BVGLINKAMAZON`,
    general: (buyerName) =>
        `Bonjou ${buyerName}!\n\nNou kontakte ou pou ba ou nouvel konsènan kont BVGLINKAMAZON ou.\n\nMèsi!\n- BVGLINKAMAZON`,
};

export const LinkAmazonBuyers = () => {
    const navigate = useNavigate();
    const [buyers, setBuyers] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, online: 0 });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [refreshing, setRefreshing] = useState(false);
    const [reminderMenu, setReminderMenu] = useState(null); // buyer id for which menu is open

    const token = localStorage.getItem("enterprise_token");
    const user = JSON.parse(localStorage.getItem("enterprise_user") || "{}");

    useEffect(() => {
        if (!token || user.role !== "super_admin") {
            navigate("/enterprise/login");
            return;
        }
        fetchData();

        // Auto-refresh every 30s for online status
        const interval = setInterval(() => {
            fetchData(true);
        }, 30000);
        return () => clearInterval(interval);
    }, [token, navigate]);

    const fetchData = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const headers = { Authorization: `Bearer ${token}` };
            const [buyersRes, statsRes] = await Promise.all([
                axios.get(`${API}/linkamazon/admin/buyers`, { headers }),
                axios.get(`${API}/linkamazon/admin/buyers/stats`, { headers })
            ]);
            setBuyers(buyersRes.data.buyers || []);
            setStats(statsRes.data);
        } catch (error) {
            console.error("Error fetching buyers:", error);
            if (!silent) toast.error("Erè nan chaje itilizatè yo");
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchData(true);
    };

    const handleToggleStatus = async (buyerId, currentStatus) => {
        try {
            const response = await axios.post(
                `${API}/linkamazon/admin/buyers/${buyerId}/toggle-status`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                toast.success(
                    response.data.new_status === "active"
                        ? "Itilizatè aktive!"
                        : "Itilizatè dezaktive!"
                );
                fetchData(true);
            }
        } catch (error) {
            toast.error("Erè nan chanje estati");
        }
    };

    const openWhatsApp = (phone, message) => {
        // Clean phone number: remove spaces, dashes, keep only digits and +
        let cleanPhone = phone.replace(/[^\d+]/g, '');
        // Remove leading + if present, WhatsApp API expects just digits
        if (cleanPhone.startsWith('+')) cleanPhone = cleanPhone.substring(1);
        // If starts with 509 (Haiti) and is 8 digits, it's ok
        // If doesn't start with country code, add 509
        if (cleanPhone.length === 8) cleanPhone = '509' + cleanPhone;

        const encodedMessage = encodeURIComponent(message);
        window.open(`https://wa.me/${cleanPhone}?text=${encodedMessage}`, '_blank');
    };

    const sendReminder = (buyer, type) => {
        const buyerName = buyer.name || 'Kliyan';
        const phone = buyer.phone;

        if (!phone) {
            toast.error(`${buyerName} pa gen nimewo telefòn!`);
            return;
        }

        const message = REMINDER_TEMPLATES[type](buyerName);
        openWhatsApp(phone, message);
        setReminderMenu(null);
        toast.success(`WhatsApp ouvri pou ${buyerName}`);
    };

    const filteredBuyers = buyers.filter((buyer) => {
        const matchesSearch =
            buyer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            buyer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            buyer.phone?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus =
            statusFilter === "all" ||
            (statusFilter === "online" && buyer.is_online) ||
            (statusFilter === "active" && buyer.status === "active") ||
            (statusFilter === "inactive" && buyer.status !== "active");
        return matchesSearch && matchesStatus;
    });

    const onlineBuyers = buyers.filter((b) => b.is_online);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("ht-HT", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatLastActive = (dateStr) => {
        if (!dateStr) return "Janm konekte";
        const d = new Date(dateStr);
        const now = new Date();
        const diffMs = now - d;
        const diffMin = Math.floor(diffMs / 60000);
        if (diffMin < 1) return "Kounye a";
        if (diffMin < 60) return `${diffMin} min de sa`;
        const diffHours = Math.floor(diffMin / 60);
        if (diffHours < 24) return `${diffHours}h de sa`;
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays}j de sa`;
    };

    if (loading) {
        return (
            <EnterpriseLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF9900]"></div>
                </div>
            </EnterpriseLayout>
        );
    }

    return (
        <EnterpriseLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#232F3E]">
                            Itilizatè LinkAmazon
                        </h1>
                        <p className="text-gray-500">
                            Jere tout itilizatè ki enskri sou BVG LinkAmazon
                        </p>
                    </div>
                    <Button
                        onClick={handleRefresh}
                        variant="outline"
                        className="gap-2"
                        disabled={refreshing}
                    >
                        <RefreshCw
                            className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                        />
                        Rafrechi
                    </Button>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <Card className="border-0 shadow-lg">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">
                                        Total Itilizatè
                                    </p>
                                    <p className="text-3xl font-black text-[#232F3E]">
                                        {stats.total}
                                    </p>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                                    <Users className="w-6 h-6 text-blue-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium">Aktif</p>
                                    <p className="text-3xl font-black text-green-600">
                                        {stats.active}
                                    </p>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                                    <UserCheck className="w-6 h-6 text-green-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="border-0 shadow-lg col-span-2 md:col-span-1 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                        <CardContent className="p-5">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-white/80 font-medium">
                                        Online Kounye a
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <p className="text-3xl font-black">{onlineBuyers.length}</p>
                                        <div className="flex items-center gap-1">
                                            <Circle className="w-2.5 h-2.5 fill-green-300 text-green-300 animate-pulse" />
                                            <span className="text-xs text-white/70">LIVE</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                                    <Wifi className="w-6 h-6 text-white" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Online Users Quick View */}
                {onlineBuyers.length > 0 && (
                    <Card className="border-0 shadow-lg border-l-4 border-l-green-500">
                        <CardHeader className="pb-3">
                            <CardTitle className="flex items-center gap-2 text-green-700 text-base">
                                <Circle className="w-3 h-3 fill-green-500 text-green-500 animate-pulse" />
                                Itilizatè Online ({onlineBuyers.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-wrap gap-2">
                                {onlineBuyers.map((b) => (
                                    <div
                                        key={b.id}
                                        className="flex items-center gap-2 bg-green-50 px-3 py-2 rounded-full border border-green-200"
                                    >
                                        <div className="relative">
                                            <div className="w-8 h-8 rounded-full bg-[#FF9900] flex items-center justify-center text-black font-bold text-xs">
                                                {(b.name || b.email || "?")[0].toUpperCase()}
                                            </div>
                                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-white" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-semibold text-gray-800">
                                                {b.name || "N/A"}
                                            </p>
                                            <p className="text-[10px] text-gray-500">{b.email}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                )}

                {/* Filter & Search */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                        <Input
                            placeholder="Chèche pa non, imèl, oswa telefòn..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                        />
                    </div>
                    <div className="flex gap-2">
                        {[
                            { key: "all", label: "Tout" },
                            { key: "online", label: "Online" },
                            { key: "active", label: "Aktif" },
                            { key: "inactive", label: "Inaktif" },
                        ].map((tab) => (
                            <button
                                key={tab.key}
                                onClick={() => setStatusFilter(tab.key)}
                                className={`px-3 py-2 rounded-lg text-sm font-bold transition-all whitespace-nowrap ${statusFilter === tab.key
                                    ? "bg-[#232F3E] text-white"
                                    : "bg-white text-gray-500 hover:bg-gray-50 border border-gray-200"
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Buyers List */}
                <div className="space-y-3">
                    {filteredBuyers.length === 0 ? (
                        <Card className="border-0 shadow-lg">
                            <CardContent className="py-12 text-center">
                                <Users className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                                <p className="text-gray-400 text-lg font-semibold">
                                    Pa gen itilizatè ki matche
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        filteredBuyers.map((buyer) => (
                            <Card
                                key={buyer.id}
                                className="border-0 shadow-md hover:shadow-lg transition-shadow"
                            >
                                <CardContent className="p-4">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                        {/* User Info */}
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                            <div className="relative flex-shrink-0">
                                                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#FF9900] to-[#FFB347] flex items-center justify-center text-black font-bold text-sm">
                                                    {(buyer.name || buyer.email || "?")[0].toUpperCase()}
                                                </div>
                                                {buyer.is_online && (
                                                    <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-green-500 rounded-full border-2 border-white" />
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="flex items-center gap-2">
                                                    <p className="font-bold text-[#232F3E] truncate">
                                                        {buyer.name || "Sans non"}
                                                    </p>
                                                    {buyer.is_online && (
                                                        <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">
                                                            ONLINE
                                                        </span>
                                                    )}
                                                    <span
                                                        className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${buyer.status === "active"
                                                            ? "bg-blue-100 text-blue-700"
                                                            : "bg-red-100 text-red-700"
                                                            }`}
                                                    >
                                                        {buyer.status === "active" ? "AKTIF" : "INAKTIF"}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                                                    <span className="flex items-center gap-1">
                                                        <Mail className="w-3 h-3" />
                                                        {buyer.email}
                                                    </span>
                                                    {buyer.phone && (
                                                        <span className="flex items-center gap-1 hidden sm:flex">
                                                            <Phone className="w-3 h-3" />
                                                            {buyer.phone}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Stats & Actions */}
                                        <div className="flex items-center gap-4 pl-14 md:pl-0">
                                            <div className="text-center">
                                                <p className="text-lg font-black text-[#232F3E]">
                                                    {buyer.order_count || 0}
                                                </p>
                                                <p className="text-[10px] text-gray-400 font-medium">
                                                    Kòmand
                                                </p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-lg font-black text-[#FF9900]">
                                                    ${buyer.total_spent || 0}
                                                </p>
                                                <p className="text-[10px] text-gray-400 font-medium">
                                                    Depanse
                                                </p>
                                            </div>
                                            <div className="text-center hidden sm:block">
                                                <div className="flex items-center gap-1 text-xs text-gray-400">
                                                    <Clock className="w-3 h-3" />
                                                    {formatLastActive(buyer.last_active)}
                                                </div>
                                                <p className="text-[10px] text-gray-400 font-medium">
                                                    Dènye Aktivite
                                                </p>
                                            </div>
                                            {/* WhatsApp Reminder Button */}
                                            <div className="relative">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setReminderMenu(reminderMenu === buyer.id ? null : buyer.id);
                                                    }}
                                                    className="rounded-lg h-9 px-3 border-green-300 text-green-700 hover:bg-green-50"
                                                    title="Voye rapèl WhatsApp"
                                                >
                                                    <MessageCircle className="w-3.5 h-3.5 mr-1" />
                                                    <span className="text-xs hidden sm:inline">Rapèl</span>
                                                </Button>

                                                {/* Dropdown Menu */}
                                                {reminderMenu === buyer.id && (
                                                    <div className="absolute right-0 top-10 z-50 bg-white rounded-xl shadow-2xl border border-gray-100 w-56 py-2 animate-in fade-in slide-in-from-top-2">
                                                        <p className="px-3 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">Voye rapèl via WhatsApp</p>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); sendReminder(buyer, 'payment'); }}
                                                            className="w-full text-left px-3 py-2.5 hover:bg-orange-50 flex items-center gap-2 text-sm transition-colors"
                                                        >
                                                            <DollarSign className="w-4 h-4 text-orange-500" />
                                                            <span>Rapèl Peman</span>
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); sendReminder(buyer, 'order_ready'); }}
                                                            className="w-full text-left px-3 py-2.5 hover:bg-blue-50 flex items-center gap-2 text-sm transition-colors"
                                                        >
                                                            <Package className="w-4 h-4 text-blue-500" />
                                                            <span>Kòmand Prè</span>
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); sendReminder(buyer, 'welcome'); }}
                                                            className="w-full text-left px-3 py-2.5 hover:bg-green-50 flex items-center gap-2 text-sm transition-colors"
                                                        >
                                                            <Bell className="w-4 h-4 text-green-500" />
                                                            <span>Mesaj Byenveni</span>
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); sendReminder(buyer, 'general'); }}
                                                            className="w-full text-left px-3 py-2.5 hover:bg-purple-50 flex items-center gap-2 text-sm transition-colors"
                                                        >
                                                            <Send className="w-4 h-4 text-purple-500" />
                                                            <span>Mesaj Jeneral</span>
                                                        </button>
                                                        <div className="border-t mt-1 pt-1">
                                                            <button
                                                                onClick={(e) => { e.stopPropagation(); setReminderMenu(null); }}
                                                                className="w-full text-left px-3 py-2 text-xs text-gray-400 hover:bg-gray-50 transition-colors"
                                                            >
                                                                Fèmen
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            <Button
                                                size="sm"
                                                variant={
                                                    buyer.status === "active" ? "destructive" : "default"
                                                }
                                                onClick={() =>
                                                    handleToggleStatus(buyer.id, buyer.status)
                                                }
                                                className="rounded-lg h-9 px-3"
                                            >
                                                {buyer.status === "active" ? (
                                                    <>
                                                        <ShieldOff className="w-3.5 h-3.5 mr-1" />
                                                        <span className="text-xs hidden sm:inline">
                                                            Dezaktive
                                                        </span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Shield className="w-3.5 h-3.5 mr-1" />
                                                        <span className="text-xs hidden sm:inline">
                                                            Aktive
                                                        </span>
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Mobile extra info */}
                                    <div className="md:hidden flex items-center gap-4 mt-2 pl-14 text-xs text-gray-400">
                                        {buyer.phone && (
                                            <span className="flex items-center gap-1">
                                                <Phone className="w-3 h-3" />
                                                {buyer.phone}
                                            </span>
                                        )}
                                        <span className="flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {formatLastActive(buyer.last_active)}
                                        </span>
                                        <span>Enskri: {formatDate(buyer.created_at)}</span>
                                    </div>
                                </CardContent>
                            </Card>
                        ))
                    )}
                </div>
            </div>
        </EnterpriseLayout>
    );
};

